import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Sparkles } from 'lucide-react';
import { HomeNav } from '../components/home/HomeNav';
import { HeroSearch } from '../components/home/HeroSearch';
import { VenueCardGrid } from '../components/home/VenueCardGrid';
import {
  TransparencyCards, HowItWorks, CalculatorShowcase, TransparencyPromise,
  CategoriesBento, CompareTeaser, RealInfo, OwnersSection, AvailabilityEducation,
  StatsAndTestimonials, HomeFAQs, FinalCTA, HomeFooter, PartnerDialog
} from '../components/home/HomeSections';
import { CustomSection } from '../components/CustomSection';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomeLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [venues, setVenues] = useState([]);
  const [showPartner, setShowPartner] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/homepage`).then(res => setContent(res.data)).catch(() => {});
    axios.get(`${API_URL}/venues/cards`).then(res => setVenues(res.data.venues || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (content && location.hash) {
      setTimeout(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
  }, [content, location.hash]);

  if (!content) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#1A3626]/20 border-t-[#1A3626] rounded-full animate-spin" />
      </div>
    );
  }

  const featured = (content.sections || []).find(s => s.key === 'featured_venues') || {};
  const featuredVenues = venues.slice(0, featured.settings?.count || 6);

  const renderSection = (s) => {
    if (s.visible === false) return null;
    if (s.type) return <CustomSection key={s.key} section={s} />;
    switch (s.key) {
      case 'transparency_cards': return <TransparencyCards key={s.key} section={s} />;
      case 'featured_venues':
        return featuredVenues.length > 0 ? (
          <section key={s.key} className="bg-white border-y border-[#1A3626]/10 py-20 md:py-24 font-wvc-body" data-testid="section-featured-venues">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
                <div>
                  <p className="text-[#C6A87C] uppercase tracking-[0.22em] text-xs font-semibold flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Featured</p>
                  <h2 className="font-wvc-display text-3xl md:text-5xl font-medium text-[#1A201C] tracking-tight mt-4">{s.heading}</h2>
                  <p className="text-[#3E4C43] mt-3">{s.copy}</p>
                </div>
                <button onClick={() => navigate('/venues')} className="text-[#1A3626] text-sm inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap" data-testid="featured-view-all">
                  View all venues <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <VenueCardGrid venues={featuredVenues} settings={s.settings} />
            </div>
          </section>
        ) : null;
      case 'how_it_works': return <HowItWorks key={s.key} section={s} />;
      case 'calculator': return <CalculatorShowcase key={s.key} section={s} />;
      case 'transparency_promise': return <TransparencyPromise key={s.key} section={s} />;
      case 'categories': return <CategoriesBento key={s.key} section={s} />;
      case 'compare': return <CompareTeaser key={s.key} section={s} />;
      case 'real_info': return <RealInfo key={s.key} section={s} />;
      case 'owners': return <OwnersSection key={s.key} section={s} onPartner={() => setShowPartner(true)} />;
      case 'availability_education': return <AvailabilityEducation key={s.key} section={s} />;
      case 'stats': return <StatsAndTestimonials key={s.key} section={s} />;
      case 'faqs': return <HomeFAQs key={s.key} section={s} />;
      case 'final_cta': return <FinalCTA key={s.key} section={s} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="home-landing">
      <HomeNav dark onPartner={() => setShowPartner(true)} />
      <HeroSearch hero={content.hero} />
      {(content.sections || []).map(renderSection)}
      <HomeFooter />
      <PartnerDialog open={showPartner} onOpenChange={setShowPartner} />
    </div>
  );
}
